/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useEffect, useState } from 'react';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { CheckmarkIcon } from '../../assets/icons';
import { extendChildrenWithProps, useTabbing } from '../../utils';
import { Animate } from '../Animate';
import { Headings } from '../Headings';
import { Container, Flex } from '../Layout';
import { useMediaMin } from '../Media';
import { createScrollReference, scrollToElement } from '../Scroll';
import { Text } from '../Text';
import { WizardStepStatus } from './types';

const Wrapper = styled.div`
  &:last-child > button {
    margin-bottom: 0;
  }
`;

const StepHeader = styled.button<{ status: WizardStepStatus; $isTabbing: boolean; $isSelectable: boolean }>`
  position: relative;
  background-color: ${theme.colors.black10};
  width: 100%;
  text-align: left;
  border: 0;
  padding: 0;
  appearance: none;
  cursor: default;
  outline: none;

  ${props =>
    (props.status === WizardStepStatus.Inactive || props.status === WizardStepStatus.Completed) &&
    css`
      margin-bottom: 0.125rem;
    `};

  ${props =>
    props.status === WizardStepStatus.Active &&
    css`
      background-color: ${theme.colors.babyBlue};
    `};

  ${props =>
    props.$isTabbing &&
    css`
      &:focus {
        outline: ${props.status === WizardStepStatus.Completed ? `${theme.colors.darkBlue} solid 2px` : 'none'};
        outline-offset: -2px;
      }
    `}

  cursor: ${props => (props.status === WizardStepStatus.Completed && props.$isSelectable ? 'pointer' : 'default')};
`;

const StyledHeaderContent = styled(Container)<{ status: WizardStepStatus }>`
  @media screen and (min-width: ${theme.breakpoint.lg}px) {
    border-left: ${props =>
      props.status === WizardStepStatus.Active
        ? `10px solid ${theme.colors.darkBlue}`
        : `10px solid ${theme.colors.black30}`};
  }
`;

const Circle = styled.div<{ status: WizardStepStatus }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 2.375rem;
  width: 1.875rem;
  height: 1.875rem;
  bottom: 0;
  margin: auto 0;
  border-radius: 50%;

  ${props =>
    props.status === WizardStepStatus.Active &&
    css`
      background-color: ${theme.colors.darkBlue};
    `}

  ${props =>
    props.status === WizardStepStatus.Completed &&
    css`
      background-color: ${theme.colors.white};
    `};

  ${props =>
    props.status === WizardStepStatus.Inactive &&
    css`
      background-color: ${theme.colors.black30};
    `};

  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    left: 1.75rem;
  }
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    left: 1.25rem;
  }
`;

type WizardStepProps = {
  testId?: string;
  name: string;
  previousStepName?: string;
  status: WizardStepStatus;
  number?: number;
  // TODO: jsdoc about what this does
  isSelectable?: boolean;
  enableScrolling?: boolean;
  onActivate?: () => void;
  children: ReactNode;
};
export const WizardStep = ({
  testId,
  name,
  previousStepName = '',
  status,
  number,
  isSelectable,
  enableScrolling = true,
  onActivate,
  children,
}: WizardStepProps) => {
  const isLargeDesktop = useMediaMin(theme.breakpoint.xlg);
  const scrollOffset = isLargeDesktop ? 0 : -70;
  const [wasStepCompleted, setWasStepCompleted] = useState(false);

  useEffect(() => {
    if (enableScrolling && status === WizardStepStatus.Active) {
      if (wasStepCompleted) {
        setWasStepCompleted(false);
        scrollTo(name);
      } else {
        // todo: why scroll to previous?
        scrollTo(previousStepName);
      }
    }
  }, [status]);

  const handleClick = () => {
    if (isSelectable && onActivate && status === WizardStepStatus.Completed) {
      setWasStepCompleted(true);
      onActivate();
    }
  };

  const scrollTo = (ref: string) =>
    setTimeout(() => {
      scrollToElement(ref, 1000, scrollOffset);
    }, 250);

  return (
    <Wrapper data-testid={testId}>
      {name && enableScrolling && createScrollReference(name)}
      {extendChildrenWithProps(children, { handleClick, isSelectable, number, status })}
    </Wrapper>
  );
};

// TODO: solve props-passing with context instead
WizardStep.Header = ({ testId, status, number, handleClick, children, isSelectable }: any) => {
  const isTabbing = useTabbing();

  return (
    <StepHeader
      data-testid={testId}
      status={status}
      onClick={handleClick}
      $isTabbing={isTabbing}
      tabIndex={status === WizardStepStatus.Completed ? 0 : -1}
      $isSelectable={!!isSelectable}
    >
      <StyledHeaderContent
        paddingLeft={[70, 80, 93]}
        paddingRight={[20, 80, 103]}
        paddingTop={28}
        paddingBottom={28}
        status={status}
      >
        <Circle status={status}>
          {status === WizardStepStatus.Completed ? (
            <CheckmarkIcon width={13} color="darkBlue" />
          ) : (
            <Text
              renderAs="span"
              fontWeight="semibold"
              size="medium"
              color={
                status === WizardStepStatus.Active
                  ? 'white'
                  : status === WizardStepStatus.Inactive
                    ? 'black70'
                    : 'white'
              }
            >
              {number}
            </Text>
          )}
        </Circle>
        <Headings.H4
          color={
            status === WizardStepStatus.Active
              ? 'black90'
              : status === WizardStepStatus.Completed
                ? 'black70'
                : status === WizardStepStatus.Inactive
                  ? 'black50'
                  : 'black90'
          }
        >
          {children}
        </Headings.H4>
      </StyledHeaderContent>
    </StepHeader>
  );
};

WizardStep.Body = ({ status, children }: any) => (
  <Animate.Height isTriggered={status === WizardStepStatus.Active}>
    <Flex backgroundColor="white">{children}</Flex>
  </Animate.Height>
);

WizardStep.MainSection = ({ children }: { children: ReactNode }) => (
  <Container
    flexBasis={784}
    flexGrow={1}
    paddingLeft={[20, 80, 103]}
    paddingRight={[20, 80, 103]}
    paddingTop={[30, 30, 40]}
    paddingBottom={80}
  >
    {children}
  </Container>
);

WizardStep.AsideSection = ({ children }: { children: ReactNode }) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  return isDesktop ? <Flex flexBasis={412}>{children}</Flex> : null;
};

WizardStep.List = ({ children }: { children: ReactNode }) => <Container flexGrow={1}>{children}</Container>;
